import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface MaintenanceRecord {
  maintenance_id: string;
  equipment_id: string;
  maintenance_type: string;
  description: string;
  performed_by?: string;
  cost?: number;
  performed_at: string;
  next_maintenance_date?: string;
  created_by: string;
  created_at: string;
}

export interface Equipment {
  equipment_id: string;
  name: string;
  equipment_code?: string;
  serial_number?: string;
  model?: string;
  manufacturer?: string;
  category?: string;
  location_id?: string;
  location_name?: string;
  department_id?: string;
  department_name?: string;
  status: string;
  purchase_date?: string;
  warranty_expiry?: string;
  expected_lifespan_years?: number;
  notes?: string;
  created_at: string;
  updated_at?: string;
  last_maintenance?: MaintenanceRecord;
  next_maintenance?: string;
  maintenance_records: MaintenanceRecord[];
}

export interface EquipmentStats {
  total: number;
  by_status: Record<string, number>;
  by_category: Record<string, number>;
  upcoming_maintenance: number;
  overdue_maintenance: number;
}

export interface UpcomingMaintenanceEquipment {
  equipment_id: string;
  name: string;
  equipment_code?: string;
  category?: string;
  location_name?: string;
  department_name?: string;
}

export function useEquipment() {
  const queryClient = useQueryClient();

  const getEquipmentList = (params?: {
    status?: string;
    category?: string;
    location_id?: string;
    department_id?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }) =>
    useQuery<Equipment[]>({
      queryKey: ['equipment', params],
      queryFn: () => api.get('/equipment', { params }).then((res) => res.data),
    });

  const getEquipment = (equipmentId: string) =>
    useQuery<Equipment>({
      queryKey: ['equipment', equipmentId],
      queryFn: () => api.get(`/equipment/${equipmentId}`).then((res) => res.data),
      enabled: !!equipmentId,
    });

  const getEquipmentStats = () =>
    useQuery<EquipmentStats>({
      queryKey: ['equipment-stats'],
      queryFn: () => api.get('/equipment/stats/summary').then((res) => res.data),
    });

  const getUpcomingMaintenance = (days: number = 30) =>
    useQuery<UpcomingMaintenanceEquipment[]>({
      queryKey: ['upcoming-maintenance', days],
      queryFn: () =>
        api.get('/equipment/upcoming/maintenance', { params: { days } }).then((res) => res.data),
    });

  const createEquipment = () =>
    useMutation({
      mutationFn: (data: {
        name: string;
        equipment_code?: string;
        serial_number?: string;
        model?: string;
        manufacturer?: string;
        category?: string;
        location_id?: string;
        department_id?: string;
        purchase_date?: string;
        warranty_expiry?: string;
        expected_lifespan_years?: number;
        notes?: string;
      }) => api.post('/equipment', data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['equipment'] });
        queryClient.invalidateQueries({ queryKey: ['equipment-stats'] });
      },
    });

  const updateEquipmentStatus = () =>
    useMutation({
      mutationFn: ({ equipmentId, status }: { equipmentId: string; status: string }) =>
        api.post(`/equipment/${equipmentId}/status`, { status }),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['equipment'] });
        queryClient.invalidateQueries({ queryKey: ['equipment', variables.equipmentId] });
        queryClient.invalidateQueries({ queryKey: ['equipment-stats'] });
      },
    });

  const addMaintenanceRecord = () =>
    useMutation({
      mutationFn: ({
        equipmentId,
        data,
      }: {
        equipmentId: string;
        data: {
          maintenance_type: string;
          description: string;
          performed_by?: string;
          cost?: number;
          next_maintenance_date?: string;
          notes?: string;
        };
      }) => api.post(`/equipment/${equipmentId}/maintenance`, data),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['equipment'] });
        queryClient.invalidateQueries({ queryKey: ['equipment', variables.equipmentId] });
        queryClient.invalidateQueries({ queryKey: ['equipment-stats'] });
        queryClient.invalidateQueries({ queryKey: ['upcoming-maintenance'] });
      },
    });

  return {
    getEquipmentList,
    getEquipment,
    getEquipmentStats,
    getUpcomingMaintenance,
    createEquipment,
    updateEquipmentStatus,
    addMaintenanceRecord,
  };
}
